/**
 * © 2025 Little Shilling, Inc.
 * LiveRegion component for screen reader announcements
 * Provides ARIA live regions for dynamic content updates
 */

import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { ARIA_LIVE } from '../../utils/accessibility';

interface LiveRegionProps {
  /** Message to announce to screen readers */
  message: string;
  /** Politeness level for announcements */
  politeness?: keyof typeof ARIA_LIVE;
  /** Whether to announce the entire region or just changes */
  atomic?: boolean;
  /** Whether the region should be visible (for debugging) */
  visible?: boolean;
  /** Additional CSS class names */
  className?: string;
}

/**
 * LiveRegion component for screen reader announcements.
 * Provides ARIA live regions for dynamic content updates.
 *
 * @example
 * // Basic usage for form success message
 * <LiveRegion
 *   message="Form submitted successfully"
 *   politeness="POLITE"
 * />
 *
 * @example
 * // Urgent error announcement
 * <LiveRegion
 *   message="Error: Please fix the required fields"
 *   politeness="ASSERTIVE"
 * />
 *
 * @param props - LiveRegion component props
 * @returns LiveRegion component for screen reader announcements
 */
const LiveRegion: React.FC<LiveRegionProps> = ({
  message,
  politeness = 'POLITE',
  atomic = true,
  visible = false,
  className,
}) => {
  const regionRef = useRef<HTMLDivElement>(null);

  // Update the region content when message changes
  useEffect(() => {
    if (regionRef.current && message) {
      // Clear and then set the message to ensure screen readers announce it
      regionRef.current.textContent = '';
      // Use a small delay to ensure the clear is processed first
      setTimeout(() => {
        if (regionRef.current) {
          regionRef.current.textContent = message;
        }
      }, 10);
    }
  }, [message]);

  return (
    <Box
      ref={regionRef}
      aria-live={politeness.toLowerCase() as 'off' | 'polite' | 'assertive'}
      aria-atomic={atomic}
      className={`${visible ? '' : 'sr-only'} ${className || ''}`}
      sx={{
        // Only apply sr-only styles if not visible
        ...(visible
          ? {}
          : {
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }),
      }}
    />
  );
};

export default LiveRegion;
